import React, { useMemo, useState, useEffect } from "react";
import styled from "styled-components";
import { Button, Input, PageHeader, Spin, Form } from "antd";
import AlertContainer from "../../../Components/AlertBoxes/alertBox";
import alertGreen from "../../../Assets/Icons/alertGreen.svg";
import alertRed from "../../../Assets/Icons/alertRed.svg";
import { getAlgorithmConfiguration, updateAlgorithmConfiguration } from "../../../Services/algoConfigServices";
import {
  DetailFields,
  HaberForm,
  PageTitleComp,
  RightLayoutContainer,
} from "../../styledElements";
import Iconmoon from "../../../Components/IconsFiles/icon";

const TABLE_COLUMNS = [
  { key: "fulfillment", label: "Fulfilment" },
  { key: "trim", label: "Trim" },
  { key: "pattern", label: "Pattern" },
  { key: "setCount", label: "Set count" },
  { key: "sheeterEfficiency", label: "Sheeter efficiency" },
];

const criteriaMap = {
  0: "Trim optimized set",
  1: "Fulfillment optimized set",
  2: "Unique pattern optimized set",
  3: "Set count optimized set",
  4: "Sheeter efficiency optimized set"
};

const defaultState = Object.keys(criteriaMap).map((key) => ({
  criteria: criteriaMap[key],
  rows: [
    { solution: "Solution 1", fulfillment: "", trim: "", pattern: "", setCount: "", sheeterEfficiency: "" },
    { solution: "Solution 2", fulfillment: "", trim: "", pattern: "", setCount: "", sheeterEfficiency: "" }
  ]
}));

const mapApiToState = (apiData) => {
  if (!apiData || Object.keys(apiData).length === 0) return defaultState;

  return Object.keys(criteriaMap).map((key) => {
    const groupData = apiData[key] || {};
    return {
      criteria: criteriaMap[key],
      rows: [
        {
          solution: "Solution 1",
          fulfillment: groupData.solution_1?.fulfilment ?? "",
          trim: groupData.solution_1?.trim ?? "",
          pattern: groupData.solution_1?.pattern ?? "",
          setCount: groupData.solution_1?.set_count ?? "",
          sheeterEfficiency: groupData.solution_1?.sheeter_efficiency ?? "",
        },
        {
          solution: "Solution 2",
          fulfillment: groupData.solution_2?.fulfilment ?? "",
          trim: groupData.solution_2?.trim ?? "",
          pattern: groupData.solution_2?.pattern ?? "",
          setCount: groupData.solution_2?.set_count ?? "",
          sheeterEfficiency: groupData.solution_2?.sheeter_efficiency ?? "",
        }
      ]
    };
  });
};

const mapStateToApi = (stateData) => {
  const apiData = {};
  stateData.forEach((group, index) => {
    apiData[index.toString()] = {
      solution_1: {
        fulfilment: Number(group.rows[0].fulfillment) || 0,
        trim: Number(group.rows[0].trim) || 0,
        pattern: Number(group.rows[0].pattern) || 0,
        set_count: Number(group.rows[0].setCount) || 0,
        sheeter_efficiency: Number(group.rows[0].sheeterEfficiency) || 0,
      },
      solution_2: {
        fulfilment: Number(group.rows[1].fulfillment) || 0,
        trim: Number(group.rows[1].trim) || 0,
        pattern: Number(group.rows[1].pattern) || 0,
        set_count: Number(group.rows[1].setCount) || 0,
        sheeter_efficiency: Number(group.rows[1].sheeterEfficiency) || 0,
      }
    };
  });
  return apiData;
};

const cloneData = (data) =>
  data.map((group) => ({
    ...group,
    rows: group.rows.map((row) => ({ ...row })),
  }));

const getCellPath = (groupIndex, rowIndex, key) => `${groupIndex}-${rowIndex}-${key}`;

const PageWrap = styled.div`
  .ant-page-header {
    padding: 0;
  }

  .ant-page-header-heading {
    align-items: center;
  }

  .algo-table-wrap {
    margin-top: 30px;
  }
`;

const TableCard = styled.div`
  background: #ffffff;
  overflow-x: auto;
`;

const ConfigTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 960px;
  border-bottom: 1px solid #edebe9;

  thead th {
    background: #f8f7f6;
    color: #605e5c;
    font-size: 14px;
    font-weight: 500;
    text-align: left;
    padding: 10px 14px;
    border-bottom: 1px solid #edebe9;
    white-space: nowrap;

    &:not(:last-child) {
      border-right: 1px solid #edebe9;
    }
  }

  tbody td {
    padding: 11px 14px;
    border-bottom: 1px solid #edebe9;
    color: #323130;
    font-size: 14px;
    vertical-align: middle;

    &:not(:last-child) {
      border-right: 1px solid #edebe9;
    }
  }
`;

const CriteriaCell = styled.td`
  width: 220px;
  color: #605e5c !important;
  vertical-align: middle !important;
`;

const SolutionCell = styled.td`
  width: 120px;
  color: #605e5c !important;
  white-space: nowrap;
`;

const ValueCell = styled.td`
  width: 130px;
  .ant-form-item {
    margin-bottom: 0 !important;
  }
  .ant-form-item-explain-error {
    font-size: 11px;
    line-height: 12px;
  }
`;

const StyledInput = styled(Input)`
  height: 28px !important;
  border: 1px solid #d2d0ce;
  border-radius: 6px;
  background: #ffffff;
  box-shadow: none;
  padding: 4px 10px !important;
  font-size: 14px;

  &:hover,
  &:focus {
    border-color: #d2d0ce;
    box-shadow: none;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  .textTypeBtn {
    margin: 0 !important;
  }

  .themeBtn {
    margin-right: 0 !important;
  }
`;

const AlgorithmConfigDetails = () => {
  const [form] = Form.useForm();
  const [isEditMode, setIsEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedData, setSavedData] = useState(defaultState);
  const [addToastr, setAddToastr] = useState(false);
  const [deleteToastr, setDeleteToastr] = useState(false);
  const [addResp, setAddResp] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      const responseData = await getAlgorithmConfiguration();
      const mappedData = mapApiToState(responseData.data.configuration);
      setSavedData(mappedData);
    } catch (error) {
      console.error(error);
      setAddResp("Failed to load configuration.");
      setDeleteToastr(true);
      setTimeout(() => setDeleteToastr(false), 3000);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleEditClick = () => {
    const defaultValues = {};
    savedData.forEach((group, groupIndex) => {
      group.rows.forEach((row, rowIndex) => {
        TABLE_COLUMNS.forEach((column) => {
          defaultValues[getCellPath(groupIndex, rowIndex, column.key)] = row[column.key];
        });
      });
    });
    form.setFieldsValue(defaultValues);
    setIsEditMode(true);
  };

  const handleCancel = () => {
    form.resetFields();
    setIsEditMode(false);
  };

  const handleSave = async () => {
    try {
      await form.validateFields();
    } catch (error) {
      return; // Validation failed
    }

    setSaving(true);
    try {
      const values = form.getFieldsValue();
      const updatedData = cloneData(savedData);

      updatedData.forEach((group, groupIndex) => {
        group.rows.forEach((row, rowIndex) => {
          TABLE_COLUMNS.forEach((column) => {
            row[column.key] = values[getCellPath(groupIndex, rowIndex, column.key)];
          });
        });
      });

      const payload = mapStateToApi(updatedData);
      await updateAlgorithmConfiguration(payload);
      setAddResp("Configuration saved successfully.");
      setAddToastr(true);
      setTimeout(() => setAddToastr(false), 3000);
      setSavedData(updatedData);
      setIsEditMode(false);
    } catch (error) {
      console.error(error);
      setAddResp("Failed to save configuration.");
      setDeleteToastr(true);
      setTimeout(() => setDeleteToastr(false), 3000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <RightLayoutContainer>
      <PageWrap>
        <PageTitleComp className="marginRightAdg">
          <PageHeader
            ghost={false}
            title="Algorithm Configuration"
            extra={[
              isEditMode ? (
                <HeaderActions key="edit-actions">
                  <Button className="textTypeBtn" onClick={handleCancel}>
                    Cancel changes
                  </Button>
                  <Button className="themeBtn" onClick={handleSave} loading={saving}>
                    <Iconmoon icon="edit" size={14} color="#ffffff" />
                    Save changes
                  </Button>
                </HeaderActions>
              ) : (
                <HeaderActions key="view-actions">
                  <Button className="themeBtn" onClick={handleEditClick}>
                    <Iconmoon icon="edit" size={14} color="#ffffff" />
                    Edit weights
                  </Button>
                </HeaderActions>
              ),
            ]}
          />
        </PageTitleComp>

        <DetailFields>
          <HaberForm>
            <Form form={form} component={false}>
              <div className="groupDetailWrap algo-table-wrap">
                  <TableCard>
                    <ConfigTable>
                      <thead>
                        <tr>
                          <th>Optimization criteria</th>
                          <th>Solution</th>
                          {TABLE_COLUMNS.map((column) => (
                            <th key={column.key}>{column.label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {savedData.map((group, groupIndex) =>
                          group.rows.map((row, rowIndex) => (
                            <tr key={`${group.criteria}-${row.solution}`}>
                              {rowIndex === 0 ? (
                                <CriteriaCell rowSpan={group.rows.length}>
                                  {group.criteria}
                                </CriteriaCell>
                              ) : null}
                              <SolutionCell>{row.solution}</SolutionCell>
                              {TABLE_COLUMNS.map((column) => (
                                <ValueCell key={getCellPath(groupIndex, rowIndex, column.key)}>
                                  {isEditMode ? (
                                    <Form.Item
                                      validateFirst
                                      name={getCellPath(groupIndex, rowIndex, column.key)}
                                      rules={[
                                        {
                                          required: true,
                                          message: "This field is required",
                                        },
                                        {
                                          pattern: /^\s*\d+(?:\.\d{1,3})?\s*$/,
                                          message: "Invalid format",
                                        },
                                        {
                                          pattern: new RegExp(/^\d+(\.\d{0,0})?$/),
                                          message: "decimal point values is not accepted",
                                        },
                                      ]}
                                    >
                                      <StyledInput type="text" />
                                    </Form.Item>
                                  ) : (
                                    row[column.key]
                                  )}
                                </ValueCell>
                              ))}
                            </tr>
                          ))
                        )}
                      </tbody>
                    </ConfigTable>
                  </TableCard>
              </div>
            </Form>
          </HaberForm>
        </DetailFields>
      </PageWrap>

      {addToastr && (
        <AlertContainer color="green toaster" icon={alertGreen} msg={addResp} />
      )}
      {deleteToastr && (
        <AlertContainer color="red toaster" icon={alertRed} msg={addResp} />
      )}
    </RightLayoutContainer>
  );
};

export default AlgorithmConfigDetails;
